import React, { useState } from 'react';
import LandingCreditCard from '../../../../components/landing.credit.card';
import { SelectionApp } from '../../../../types/app';
import { FORM_FIELDS, FORM_FIELDS_PHONES, PAGE } from '../../../../constants';
import { getCommonServerSideProps } from '../../../../helpers/serverside.props';
import { getLocationByRoute } from '../../../../services/location';
import { rates } from '../../../../helpers/rates';
import { PERIOD_SOURCE, PURPOSE_SOURCE_VALUES } from '../../../../constants/sources';
import {
    convertEmployerType,
    formatPhonesImmutable,
    getGlobalRegionForForm,
    getGlobalRegionFormFields,
    logProfileAndIdentityPhonesError,
    removeUndefined,
    samePhones,
} from '../../../../helpers';
import { BreadCrumb } from '../../../../components/landing/footer';
import { useUser } from '../../../../store/reducers/user';
import { useIsMobile, useIsTablet } from '@sravni/react-utils';
import { getVitrinsProductsByIds, VitrinaProductType } from '../../../../services/vitrins';
import { getCreditCardProductById } from '../../../../services/creditProduct';
import { getSelectionMappedId, getVitrinaProductInfoFromAffSub3 } from '../../../../helpers/vitrina';
import { getOrganizationById } from '../../../../services/organizations';
import { loadProfileData } from '../../../../services/profile.graphql';

interface Props {
    formData: FE.FormData;
    rateMin: number;
    rateMax: number;
    isScrollToForm: boolean;
    cookies: Record<string, string>;
    heading?: string;
    breadcrumbs?: Array<BreadCrumb>;
}

const CardsLanding: React.FC<Props> = (props) => {
    const account = useUser();
    const userId = account?.sub;
    const isTablet = useIsTablet();
    const isMobile = useIsMobile();
    const [autoRun] = useState(Boolean(account));

    return (
        <LandingCreditCard
            formData={props.formData}
            userId={userId}
            isTablet={isTablet}
            isMobile={isMobile}
            autoRun={autoRun}
            cookies={props.cookies}
        />
    );
};

const logIt = (data: Record<string, any>) => {
    const marker = 'selection_frontend_karty_landing';
    console.log(JSON.stringify({ ...data, marker }));
};

export const getServerSideProps = async (ctx: SelectionApp.PageContext): Promise<{ props: Props }> => {
    const {
        anchorToForm,
        location,
        period,
        aff_sub3,
        productId: productIdParam,
        productType: productTypeParam,
    } = ctx.query || {};

    const vitrinaProductInfo = getVitrinaProductInfoFromAffSub3((aff_sub3 as string) || '');

    const vitrinaProductId = vitrinaProductInfo.productId || (productIdParam as string) || null;
    const productType = vitrinaProductInfo.productType || (productTypeParam as string) || null;

    const getVitrinaProduct = async (productType: VitrinaProductType, id: string) => {
        try {
            const data = await getVitrinsProductsByIds(productType, [id]);
            if (data) {
                return data.items[0];
            }
            return null;
        } catch (e) {
            return null;
        }
    };

    const getSelectionProduct = async (productType: VitrinaProductType, id: string) => {
        try {
            if (productType === VitrinaProductType.CREDIT_CARD) {
                const offer = await getCreditCardProductById(id.split('--')[0]);
                if (!offer) return null;
                // Проверка, используется ли в продукте подбора та же платежная система
                if (
                    offer._id === id ||
                    (offer._id === id.split('--')[0] && !!offer.kind && offer.kind.includes(id.split('--')[1] || ''))
                ) {
                    return offer;
                }
                return null;
            } else {
                return null;
            }
        } catch (e) {
            return null;
        }
    };

    const selectionProductId = vitrinaProductId ? getSelectionMappedId(vitrinaProductId) : null;
    const [
        { props: commonPageProps },
        { [FORM_FIELDS.PHONE]: profilePhone, updated, ...profileData },
        vitrinaProduct,
        selectionProduct,
        locationsByRoute,
    ] = await Promise.all([
        getCommonServerSideProps(PAGE.CARDS_LANDING)(ctx),
        loadProfileData(ctx),
        productType && vitrinaProductId
            ? getVitrinaProduct(productType as VitrinaProductType, vitrinaProductId)
            : Promise.resolve(null),
        productType && selectionProductId
            ? getSelectionProduct(productType as VitrinaProductType, selectionProductId)
            : Promise.resolve(null),
        // Проверка что location не string[]
        typeof location === 'string' ? getLocationByRoute(location) : Promise.resolve(null),
    ]);

    if (vitrinaProductId && !vitrinaProduct) {
        logIt({ level: 'info', message: `Vitrina Product (id=${vitrinaProductId} type=${productType}) not found` });
    }

    if (selectionProductId && !selectionProduct) {
        logIt({ level: 'info', message: `Selection Product id=${selectionProductId} not found` });
    }

    const organization = vitrinaProduct ? await getOrganizationById(vitrinaProduct.organization) : null;

    if (vitrinaProduct && !organization) {
        logIt({ level: 'info', message: `organization for id=${vitrinaProduct.organization} is not found` });
    }

    const { max: rateMax, min: rateMin } = rates;
    const userIdentityPhone = commonPageProps.identityPhone;
    const withLanding = !profileData[FORM_FIELDS.AGREEMENT];

    const getIsScrollToForm = () => {
        const { tablet: isTablet, phone: isPhone } = commonPageProps.deviceInfo;

        if (isTablet && isPhone) {
            return false;
        }

        return typeof anchorToForm !== 'undefined';
    };

    const getSelectionPeriod = () => {
        if (
            withLanding &&
            typeof period === 'string' &&
            PERIOD_SOURCE.map(({ value }) => value).includes(period.toUpperCase())
        ) {
            return period.toUpperCase();
        }

        return profileData[FORM_FIELDS.PERIOD];
    };

    const userHasValidProfile = Boolean(
        commonPageProps.userId && samePhones(userIdentityPhone, profilePhone as string),
    );

    const globalRegion =
        Array.isArray(locationsByRoute) && locationsByRoute.length > 0
            ? locationsByRoute[0]
            : await getGlobalRegionForForm({
                  currentLocation: commonPageProps.initialState.locations.currentLocation,
                  useCurrentLocation: commonPageProps.isLocationManuallySet,
                  defaultLocation: {
                      id: profileData[FORM_FIELDS.REGION_ID] as number | undefined,
                      name: profileData[FORM_FIELDS.REGION] as string | undefined,
                  },
              });

    if (commonPageProps.userId && !userHasValidProfile) {
        const profile = {
            [FORM_FIELDS.PHONE]: profilePhone,
            ...profileData,
        };
        logProfileAndIdentityPhonesError(ctx.req.__USER__, profile, commonPageProps);
    }

    const formData = {
        [FORM_FIELDS.MONTHLY_SALARY]: 50000,
        ...(userHasValidProfile ? profileData : {}),
        [FORM_FIELDS.PURPOSE]: PURPOSE_SOURCE_VALUES.MONEY,
        [FORM_FIELDS.PERIOD]: getSelectionPeriod(),
        ...getGlobalRegionFormFields(globalRegion),
        [FORM_FIELDS.PHONE]: userIdentityPhone || '',

        ...(profileData[FORM_FIELDS.EMPLOYER_TYPE]
            ? { [FORM_FIELDS.EMPLOYER_TYPE]: convertEmployerType(profileData[FORM_FIELDS.EMPLOYER_TYPE] as string) }
            : {}),
    };

    return removeUndefined({
        props: {
            ...commonPageProps,
            initialState: {
                ...commonPageProps.initialState,
                locations: {
                    ...commonPageProps.initialState.locations,
                    currentLocation: globalRegion || commonPageProps.initialState.locations.currentLocation,
                },
                autoSendProduct: {
                    ...commonPageProps.initialState.autoSendProduct,
                    organization,
                    selectionProductId,
                    selectionData: selectionProduct,
                    vitrinaProductId,
                    vitrinaData: {
                        ...commonPageProps.initialState.autoSendProduct.vitrinaData,
                        type: productType,
                        product: vitrinaProduct,
                    },
                },
                ...(updated
                    ? {
                          formStore: {
                              ...commonPageProps.initialState.formStore,
                              profileDataUpdated: updated,
                          },
                      }
                    : {}),
            },
            formData: formatPhonesImmutable(formData, FORM_FIELDS_PHONES),
            isScrollToForm: getIsScrollToForm(),
            heading: commonPageProps.initialState.metadata?.seo?.heading,
            breadcrumbs: commonPageProps.initialState.metadata?.seo?.breadcrumbs,
            rateMax,
            rateMin,
        },
    });
};

export default CardsLanding;
